import {
    RELATION_LABELS,
    TopPartner,
} from '@/lib/api/financial/financial-dashboard-api'
import { MdArrowForward } from 'react-icons/md'

interface TopPartnersTableProps {
    title: string
    subtitle?: string
    items: TopPartner[]
    emptyLabel?: string
    accentColor?: string
    /**
     * Abre a listagem completa. Ausente, o card fica só com o resumo — é o que
     * acontece onde não há recorte paginável correspondente.
     */
    onSeeAll?: () => void
}

const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(cents / 100)

export default function TopPartnersTable({
    title,
    subtitle,
    items,
    emptyLabel = 'Sem registros no período.',
    accentColor = '#3b82f6',
    onSeeAll,
}: TopPartnersTableProps) {
    const list = items ?? []
    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-5 flex flex-col gap-3">
            <div>
                <h3 className="text-lg font-semibold text-base-content">{title}</h3>
                {subtitle && (
                    <p className="text-sm text-base-content/60">{subtitle}</p>
                )}
            </div>

            {list.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-sm text-base-content/50">
                    {emptyLabel}
                </div>
            ) : (
                <ul className="flex flex-col divide-y divide-base-300/50 flex-1">
                    {list.map((item, idx) => (
                        <li
                            key={`${item.relation}-${item.id}`}
                            className="flex items-center justify-between gap-3 py-2"
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <span
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
                                    style={{ backgroundColor: accentColor }}
                                >
                                    {idx + 1}
                                </span>
                                <div className="min-w-0 tooltip tooltip-top text-left" data-tip={item.name}>
                                    <p className="text-sm font-medium truncate">
                                        {item.name}
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        {RELATION_LABELS[item.relation]} · {item.count} lançamento(s)
                                    </p>
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-sm font-semibold">
                                    {formatCurrency(item.totalAmount)}
                                </p>
                                {item.totalPaidAmount > 0 && (
                                    <p className="text-xs text-base-content/60">
                                        Pago: {formatCurrency(item.totalPaidAmount)}
                                    </p>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* O card mostra os cinco primeiros; quem precisa do resto (e do
                total) abre a listagem — que herda o mesmo recorte da tela. */}
            {onSeeAll && list.length > 0 && (
                <button
                    type="button"
                    onClick={onSeeAll}
                    className="btn btn-ghost btn-sm justify-between border border-base-300/70 mt-auto"
                >
                    Ver todos
                    <MdArrowForward className="text-base" />
                </button>
            )}
        </div>
    )
}
